import * as Sentry from "integrations/sentry";
import get from "lodash/get";
import { channel } from "redux-saga";
import { put, takeLatest } from "redux-saga/effects";
import { createApiSaga } from "redux-store/api-sagas/sagas";
import { INIT_APP } from "redux-store/init/actions";
import {
  LOAD_USERS,
  LOAD_USERS_FAILURE,
  LOAD_USERS_SUCCESS,
} from "redux-store/users/actions";
import { userService } from "service/user";
import { auth } from "store/auth";

import {
  AUTH_GET_TOKEN,
  AUTH_GET_TOKEN_FAILURE,
  AUTH_GET_TOKEN_SUCCESS,
  authTokenChangeAction,
  USER_LOGOUT,
  userLogin,
} from "./actions";

/*
 * Synchronize the auth token; the redux store with the
 * auth store (window localStorage) so the app can respond (rerender)
 * to authentication events.
 */
const authTokenChannel = channel();
auth.subscribe(onTokenChange);

function onTokenChange(token) {
  authTokenChannel.put(authTokenChangeAction(token));
}

function* watchAuthToken() {
  yield takeLatest(authTokenChannel, put);
}

/*
 * Authentication saga.
 *
 * On app init we call the users/ endpoint to retrieve the list of
 * account users. This succeeds or fails based upon the token retrieved
 * from auth store when making the request via base service.
 *
 * On failure -> 401 status code.
 *   The genericApiCaller will emit USER_LOGOUT.
 *   We clear the token on the auth store here.
 *
 * On success -> 200 status code.
 *   authGetUsersSaga will emit LOAD_USERS_SUCCESS.
 *   We will extract the current user here.
 *   We set the token on the auth store.
 *   We then emit USER_LOGIN, which is a trigger to reloadApiSagas.
 */
function* _authSaga(action) {
  const { payload, type } = action;

  if (type === LOAD_USERS_SUCCESS) {
    const data = get(payload, "data", []);
    const user = data.find((u) => u.current_user);

    Sentry.setUser(user);

    if (!user) {
      console.error("No authenticated user.", payload);
      return;
    }

    auth.setToken(user.token);
    yield put(userLogin(user, payload.response, payload.message));

    return;
  }

  if (type === USER_LOGOUT) {
    Sentry.setUser(null);

    auth.clearToken();

    return;
  }

  if (type === AUTH_GET_TOKEN_SUCCESS) {
    const token = get(payload, "response.data.token");

    auth.setToken(token);

    yield put({ type: LOAD_USERS });

    return;
  }

  console.warn("Unhandled auth action", action);
}

function* watchAuthSaga() {
  yield takeLatest(
    [AUTH_GET_TOKEN_SUCCESS, LOAD_USERS_SUCCESS, USER_LOGOUT],
    _authSaga,
  );
}

// This populates the users store. It's kind of weird how we authenticate.
const authGetUsersSaga = createApiSaga({
  fetchAction: [LOAD_USERS, INIT_APP],
  successAction: LOAD_USERS_SUCCESS,
  failureAction: LOAD_USERS_FAILURE,
  serviceName: "userService.load",
  reload: false,
  service: userService.load,
});

const authLoginSaga = createApiSaga({
  fetchAction: AUTH_GET_TOKEN,
  successAction: AUTH_GET_TOKEN_SUCCESS,
  failureAction: AUTH_GET_TOKEN_FAILURE,
  serviceName: "userService.login",
  reload: false,
  service: userService.login,
});

const authSagas = [
  watchAuthSaga(),
  watchAuthToken(),
  authGetUsersSaga(),
  authLoginSaga(),
];

export {
  _authSaga,
  authGetUsersSaga,
  authLoginSaga,
  authSagas,
  watchAuthSaga,
  watchAuthToken,
};
